import { useI18n } from '@/i18n'
import { useMediaQuery } from '@/shared/hooks/useMediaQuery'
import { X } from '@/shared/icons/phosphor'
import {
  CloseButton as KobalteCloseButton,
  Content as KobalteContent,
  Description as KobalteDescription,
  Overlay as KobalteOverlay,
  Portal as KobaltePortal,
  Root as KobalteRoot,
  Title as KobalteTitle,
  Trigger as KobalteTrigger,
} from '@kobalte/core/dialog'
import { type Component, type JSX, Show, children, onCleanup } from 'solid-js'
import { createScrollEdges } from '../../primitives/ScrollArea/createScrollEdges'
import { createFocusReturn } from './focusReturn'
import { REVEAL_SCROLLER_ATTR, createWrappedFocusReveal } from './revealWrappedFocus'

export const TOAST_STACK_HEIGHT_VAR = '--ds-toast-stack-height'

const TOAST_STACK_HEIGHT = `var(${TOAST_STACK_HEIGHT_VAR}, 0px)`

const PHONE_QUERY = '(max-width: 639px)'

const SCROLL_FADE = '1.5rem'

const revealWrappedFocus = createWrappedFocusReveal(28)

type DialogSize = 'sm' | 'md' | 'lg' | 'xl' | 'full'

const sizeClasses: Record<DialogSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
  full: 'max-w-[90vw]',
}

export interface DialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string | JSX.Element
  children: JSX.Element
  footer?: JSX.Element
  size?: DialogSize
  maxHeight?: string
  initialFocusEl?: () => HTMLElement | undefined
  finalFocusEl?: () => HTMLElement | undefined
  hideCloseButton?: boolean
  scrollFade?: boolean
  dismissOnInteractOutside?: boolean
}

export const Dialog: Component<DialogProps> = (props) => {
  const { t } = useI18n()
  const size = () => props.size ?? 'md'
  const phone = useMediaQuery(PHONE_QUERY)
  const maxHeight = () => {
    const fallback = phone() ? '100%' : '85vh'
    const own = props.maxHeight && props.maxHeight !== 'auto' ? props.maxHeight : fallback
    return `min(${own}, calc(100% - 2rem))`
  }
  const dismissOnInteractOutside = () => props.dismissOnInteractOutside ?? true
  const footer = children(() => props.footer)
  const bodyEdges = createScrollEdges('y')
  const scrollFade = () => props.scrollFade ?? true
  const bodyMask = () => {
    if (!scrollFade() || (!bodyEdges.start() && !bodyEdges.end())) {
      return undefined
    }
    const start = bodyEdges.start() ? SCROLL_FADE : '0px'
    const end = bodyEdges.end() ? SCROLL_FADE : '0px'
    return `linear-gradient(to bottom, transparent 0, #000 ${start}, #000 calc(100% - ${end}), transparent 100%)`
  }
  const attachBody = (body: HTMLDivElement) => {
    bodyEdges.attach(body)
    const contentChanges = new MutationObserver(() => bodyEdges.update())
    contentChanges.observe(body, { childList: true, subtree: true, characterData: true })
    onCleanup(() => contentChanges.disconnect())
  }

  let closeButtonRef: HTMLButtonElement | undefined
  let contentRef: HTMLElement | undefined
  const focusReturn = createFocusReturn()

  // Kobalte's own autofocus is suppressed here, so a plain `autofocus` on a
  // field would silently do nothing and the close button would take focus
  // instead, leaving the dialog open with Enter wired to "close".
  const focusInitialTarget = () => {
    const target =
      props.initialFocusEl?.() ??
      contentRef?.querySelector<HTMLElement>('[autofocus]') ??
      closeButtonRef ??
      contentRef
    target?.focus()
  }

  // Kobalte fires this before an outer dialog's focus trap has let go, so
  // focusing synchronously gets pulled back out of a nested dialog.
  const handleOpenAutoFocus = (event: Event) => {
    event.preventDefault()
    focusReturn.captureOpener()
    queueMicrotask(focusInitialTarget)
  }

  const handleCloseAutoFocus = (event: Event) => {
    focusReturn.returnFocus(event, props.finalFocusEl?.())
  }

  const handleInteractOutside = (event: Event) => {
    if (!dismissOnInteractOutside()) {
      event.preventDefault()
      return
    }
    const target = event.target as Element | null
    if (target?.closest?.('[data-scope="date-picker"], [data-toast]')) {
      event.preventDefault()
    }
  }

  return (
    <KobalteRoot open={props.open} onOpenChange={props.onOpenChange} modal>
      <KobaltePortal>
        <KobalteOverlay class="fixed inset-0 bg-[var(--color-modal-scrim)] z-50 dialog-overlay" />
        <div
          data-dialog-positioner
          class="fixed inset-x-0 top-0 z-50 flex items-center justify-center pointer-events-none transition-[bottom] duration-[var(--duration-normal)] ease-[var(--ease-out)] motion-reduce:transition-none"
          style={{ bottom: TOAST_STACK_HEIGHT }}
        >
          <KobalteContent
            class={`
            relative pointer-events-auto
            bg-[var(--color-surface)] rounded-[var(--radius-surface)]
            border border-[var(--color-border)]
            shadow-[var(--shadow-float)] p-6 sm:p-8 w-[calc(100%-2rem)] ${sizeClasses[size()]}
            dialog-content flex flex-col overflow-hidden
            transition-[max-height] duration-[var(--duration-normal)] ease-[var(--ease-out)]
            motion-reduce:transition-none
          `}
            style={{ 'max-height': maxHeight() }}
            ref={(el: HTMLElement) => {
              contentRef = el
            }}
            onOpenAutoFocus={handleOpenAutoFocus}
            onCloseAutoFocus={handleCloseAutoFocus}
            onInteractOutside={handleInteractOutside}
          >
            <div class="flex-shrink-0 pb-6">
              <div class="flex items-center justify-between gap-4">
                <KobalteTitle class="ds-section-title">{props.title}</KobalteTitle>

                <Show when={!props.hideCloseButton}>
                  <KobalteCloseButton
                    ref={closeButtonRef}
                    aria-label={t('designSystem.dialog.close')}
                    class="
                    p-2 -my-2 -mr-2 rounded-[var(--radius-12)]
                    text-[color:var(--color-text-muted)]
                    hover:bg-[var(--color-neutral-100)]
                    hover:text-[color:var(--color-text)]
                    transition-colors duration-[var(--duration-fast)]
                    focus-visible:outline-2 focus-visible:-outline-offset-2
                    focus-visible:outline-[var(--color-primary-500)]
                  "
                  >
                    <X class="w-5 h-5 pointer-events-none" />
                  </KobalteCloseButton>
                </Show>
              </div>
              {props.description && (
                <KobalteDescription class="mt-1 text-pretty text-[length:var(--text-sm)] text-[color:var(--color-text-muted)]">
                  {props.description}
                </KobalteDescription>
              )}
            </div>
            <div
              ref={attachBody}
              {...{ [REVEAL_SCROLLER_ATTR]: '' }}
              data-scroll-start={bodyEdges.start() ? '' : undefined}
              data-scroll-end={bodyEdges.end() ? '' : undefined}
              class={`peer min-h-0 overflow-y-auto empty:hidden ${scrollFade() ? 'scroll-py-7' : ''}`}
              style={{ 'mask-image': bodyMask(), '-webkit-mask-image': bodyMask() }}
              onFocusIn={revealWrappedFocus}
            >
              {props.children}
            </div>
            <Show when={footer()}>
              <div class="mt-4 flex-shrink-0 border-t border-[var(--color-border)] pt-4 peer-empty:mt-0 peer-empty:border-t-0 peer-empty:pt-0">
                {footer()}
              </div>
            </Show>
          </KobalteContent>
        </div>
      </KobaltePortal>
    </KobalteRoot>
  )
}

export const DialogTrigger = KobalteTrigger
